import { CALLBACK_ERROR_ARGUMENT, ERROR_ACTION, WAIT_FOR_ACTION } from 'redux-wait-for-action';

import { ACTION_STAGES, NON_STANDARD_ACTION_STAGES } from 'constants/actions';

/**
 * Simple identity function.
 *
 * @method _identity
 * @param  {Any}    value
 * @return {Any}
 */
const _identity = value => value;

/**
 * Create an (FSA) action that is to be dispatched via redux.
 *
 * NOTE: This is a fork of redux-actions.createAction() functionality.
 * Edited for our internal consumption to be used alongside `redux-wait-for-action` package.
 *
 * @method createAction
 * @see {@link https://github.com/redux-utilities/redux-actions}
 * @param  {string}          type           The redux action type
 * @param  {Function}        payloadCreator Function to create the payload for the action
 * @param  {Function}        metaCreator    Function to create the meta for the action
 * @param  {Object}          actionProps    Set of extra properties for the action
 * @return {Function|Object}
 */
export const createAction = (
    type = null,
    payloadCreator = _identity,
    metaCreator = null,
    actionProps = null
) => {
    // The final payload creator for the current action.
    const finalPayloadCreator =
        payloadCreator == null || payloadCreator === _identity
            ? _identity
            : (head, ...args) => (head instanceof Error ? head : payloadCreator(head, ...args));
    /**
     * The main action creator function call.
     *
     * @method actionCreator
     * @return {Object|Function}
     */
    const actionCreator = (...args) => {
        // Build the payload of the next action.
        const payload = finalPayloadCreator(...args);
        // Create the next action, add the type and any additional `actionProps` to it.
        const action = {
            type,
            ...actionProps
        };
        // If the incoming `payload` is an instanceof an Error class,
        // or the current 'action type' ends with the `/error` string,
        // then this is an error action.
        if (payload instanceof Error || type.endsWith('/error')) {
            action.error = true;
        }
        // As long as `payload` is not `undefined` we will add it to the action.
        // NOTE: This includes payloads of `null` value.
        if (payload !== undefined) {
            action.payload = payload;
        }
        // If the `metaCreator` is a valid function, then we will invoke it,
        // and add it to our action.
        if (typeof metaCreator === 'function') {
            action.meta = metaCreator(...args);
        }
        // Lastly, return the built action.
        return action;
    };
    // Add a `toString` method to the `actionCreator`
    actionCreator.toString = () => type.toString();
    // Finally, return the `actionCreator`.
    return actionCreator;
};

/**
 * Create multiple FSA (flux standard action) for a given `module` and `actionType`.
 * We will map over our internal ACTION_STAGES for each actionType,
 * creating an FSA (function) alongside it's `actionType` (string) counterpart.
 *
 * @method createActions
 * @see    {@link https://github.com/redux-utilities/redux-actions}
 * @see    {@link https://github.com/redux-utilities/flux-standard-action}
 * @param  {string}      [module='']      The module name
 * @param  {string}      [actionType='']  The actionType to create actions for
 * @param  {Object}      [options={}]     The set of actions passed to the createActions
 * @return {Object}                       Map of the created actions
 */
export const createActions = (module = '', actionType = '', options = {}) => {
    // Extract the `payloadCreator` function from the passed in options.
    const { payloadCreator, metaCreator } = options;
    // Identify if we need to promisify the current action.
    const promisifyAction = options.asPromise
        ? {
            // By default the redux-wait-for-action returns the `action.error` key
            // if an has an Error, we're overwriting that to return the entire payload instead.
            [CALLBACK_ERROR_ARGUMENT]: action => action.payload,
            // The redux-wait-for-action's error action to listen for.
            [ERROR_ACTION]           : `@@thr-${module}/${actionType}/error`,
            // The redux-wait-for-action's success action to listen for.
            [WAIT_FOR_ACTION]        : `@@thr-${module}/${actionType}/success`
        }
        : null;
    // This is our creator object, it'll create an FSA for us by passing in a `stage`.
    const creator = stage =>
        createAction(
            `@@thr-${module}/${actionType}/${stage}`,
            payloadCreator,
            metaCreator,
            promisifyAction
        );
    // We want to create a FSA for each of our `ACTION_STAGES`.
    return ACTION_STAGES.reduce((result, stage) => {
        // Create the action beforehand
        const actionCreator = creator(stage);
        // Return the result alongside the `actionCreator` and its `toString` counterpart.
        return Object.assign(result, {
            // Define the redux `action` (fired in sagas, etc.).
            [stage.toLowerCase()]: actionCreator,
            // Define the redux `actionType`.
            [stage.toUpperCase()]: actionCreator.toString()
        });
    }, creator(ACTION_STAGES[0]));
};

/**
 * Update the currently fired action's suffix, removing any non-standard suffixes.
 * Non-standard suffixes include TRIGGER, REQUEST, FULFILL.
 *
 * @method getActionWithSuffix
 * @param  {string}            suffix The suffix to attach to the action
 * @return {Function}
 */
export const getActionWithSuffix = (suffix = '') => (actionType = null) => {
    // Throw an error if we do not have an action type present.
    if (!actionType) {
        throw new Error(`A valid action needs to contain a 'type' key which is not null`);
    }
    // Define the final `actionType`.
    let finalActionType = actionType;
    // We gotta make sure the `finalActionType` does not have a non-standard suffix,
    // if so then we'll replace it with the passed in suffix.
    NON_STANDARD_ACTION_STAGES.forEach(stage => {
        // If the `finalActionType` includes a non-standard suffix,
        // we'll replace that with the SUCCESS, ERROR, or ABORT suffix.
        if (finalActionType.includes(stage)) {
            finalActionType = finalActionType.replace(stage, suffix);
        }
    });
    // Return the `finalActionType` in the end.
    return finalActionType;
};
